export const emptyWiki = {
  title: "Знаний пока нет",
  description:
    "Загрузите документы, чтобы начать собирать знания организации.",
  action: { href: "/documents", label: "Загрузить документ" },
} as const;

export const emptyWikiReview = {
  title: "Очередь пуста",
  description: "Нет фактов на проверке.",
  action: { href: "/wiki", label: "К Wiki" },
} as const;

export const wikiReviewCopy = {
  title: "Проверка знаний",
  description: "Подтвердите, поправьте формулировку или отклоните факты.",
  nav: "На проверку",
  verify: "Подтвердить",
  reject: "Отклонить",
  pending: "Сохраняем…",
  entityLabel: "Сущность",
  sourceLabel: "Источник",
  excerptLabel: "В документе",
  excerptEmpty: "Фрагмент документа недоступен.",
  textLabel: "Версия факта в новом изложении",
  emptyText: "Введите формулировку",
  queueLabel: "Очередь",
  queueSearch: "Найти в очереди",
  queueEmpty: "Нет таких фактов.",
  tooLong: "Слишком длинный текст",
  errorTitle: "Проверка",
  denied: "Недостаточно прав",
  missing: "Факт не найден",
  notQueued: "Этот факт уже не в очереди",
  openSource: "К источнику",
} as const;

export const wikiReviewHelp = {
  title: "Инструкция",
  sections: [
    {
      heading: "Как проверять",
      body: "Сверьте формулировку с фрагментом документа. Можно открыть полный источник. Правка текста Wiki документ не меняет — у факта появится метка «изменён». «Подтвердить» — факт верный, его видно в Wiki. «Отклонить» — ошибка или неактуально, факт скрывается, правки не сохраняются.",
    },
    {
      heading: "Противоречие",
      body: "Два факта про одно и то же с разными числами. Система не выбирает «победителя». Если это одно правило (пароль 8 и 12) — подтвердите актуальное, отклоните лишнее. Если оба правдивы, но про разное (зарплата секретаря в разных отделах) — подтвердите оба, верный факт не отклоняйте. Развести сущности пока нельзя.",
    },
    {
      heading: "Уверенность",
      body: "Оценка системы, не доказательство. Ниже 20% факты не сохраняются. Сами помечаем подтверждённым, только если формулировка опирается на текст источника. Иначе — очередь. В ответы чата идут подтверждённые факты и противоречия, черновики — нет.",
    },
  ],
} as const;

export const wikiEntityHelp = {
  title: "Инструкция",
  sections: [
    {
      heading: "Факты",
      body: "Слева — формулировки из доступных вам документов. Нажмите факт, чтобы справа открылись связанные противоречия.",
    },
    {
      heading: "Противоречия",
      body: "Два факта про одно и то же с разными числами. Система не выбирает «победителя». Откройте источники и решите, что актуально.",
    },
    {
      heading: "Связи",
      body: "Другие сущности, с которыми связана эта. Карта показывает ближайших соседей.",
    },
  ],
} as const;

export const wikiListCopy = {
  searchLabel: "Найти в Wiki",
  searchPlaceholder: "Название или факт",
  viewRecent: "Недавние",
  viewConflicts: "Конфликты",
  viewsLabel: "Wiki",
  emptyFilterTitle: "Ничего не найдено",
  emptyFilterDescription: "Измените запрос.",
  emptyConflictsTitle: "Нет противоречий",
  emptyConflictsDescription: "Сейчас нет фактов, которые расходятся по числам.",
  columnName: "Сущность",
  columnCount: "Факты",
  columnConflict: "Противоречие",
  conflictIcon:
    "Противоречие в фактах: формулировки не сходятся.",
  facts: "Факты",
  factsAll: "Все",
  factsFold: "Свернуть",
  relations: "Связи",
  relationsGraph: "Карта связей",
  relationsGraphOpen: "Открыть карту",
  relationsGraphBack: "К фактам",
  relationsGraphHint:
    "Перетащите узел, колесо — масштаб. Найдите узел в поле на карте.",
  relationsGraphEmpty: "У этой сущности пока нет связей.",
  relationsGraphZoomIn: "Крупнее",
  relationsGraphZoomOut: "Мельче",
  relationsGraphZoomReset: "Сбросить",
  relationsGraphDepth: "Глубина",
  relationsGraphSearch: "Найти узел",
  relationsGraphSearchEmpty: "Нет таких узлов.",
  relationsGraphLegendHere: "Эта сущность",
  relationsGraphLegendBranch: "Ветки от неё",
  relationsGraphLegendSize: "Крупнее — больше связей и фактов",
  relationsGraphLegendConflict: "Противоречие в фактах",
  relationsGraphLegendNodes: "Узлы",
  relationsGraphLegendLinks: "Связи",
  relationsGraphLegendParty: "сторона",
  relationsGraphLegendBelongs: "входит",
  relationsGraphLegendRefers: "ссылка",
  relationsGraphLegendSupersedes: "замена",
  relationsSearch: "Найти связь",
  relationsEmpty: "Нет таких связей.",
  sourceMeta: "Wiki",
  open: "Открыть в Wiki",
  conflictsTitle: "Противоречия",
  conflictsEmpty: "У этого факта нет противоречий.",
  conflictsHint: "Выберите факт с меткой, чтобы сравнить формулировки и источники.",
  openFact: "К факту",
} as const;

export type WikiCatalogView = "recent" | "conflicts";

export function parseWikiCatalogView(raw: string | undefined): WikiCatalogView {
  return raw === "conflicts" ? "conflicts" : "recent";
}

export function wikiCatalogHref(params?: {
  view?: WikiCatalogView;
  q?: string;
}): string {
  const search = new URLSearchParams();
  if (params?.view === "conflicts") {
    search.set("tab", "conflicts");
  }
  const query = params?.q?.trim() ?? "";
  if (query) {
    search.set("q", query);
  }
  const qs = search.toString();
  return qs ? `/wiki?${qs}` : "/wiki";
}

export const wikiCatalogViews = [
  { id: "recent" as const, label: wikiListCopy.viewRecent },
  { id: "conflicts" as const, label: wikiListCopy.viewConflicts },
];

export const WIKI_CATALOG_SEARCH_MS = 300;
export const WIKI_PAGE_SIZE = 10;
export const WIKI_PAGE_SIZES = [10, 20, 50] as const;

export type WikiListSortKey = "name" | "count";
export type WikiListSort = { key: WikiListSortKey; dir: "asc" | "desc" };

export function nextWikiListSort(
  current: WikiListSort | null,
  key: string,
): WikiListSort {
  const nextKey: WikiListSortKey = key === "count" ? "count" : "name";
  if (current?.key === nextKey) {
    return { key: nextKey, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key: nextKey, dir: nextKey === "count" ? "desc" : "asc" };
}

export function sortWikiListRows<
  T extends { name: string; claimCount: number },
>(rows: T[], sort: WikiListSort): T[] {
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    if (sort.key === "count") {
      return sign * (left.claimCount - right.claimCount);
    }
    return sign * left.name.localeCompare(right.name, "ru");
  });
}

export function matchesWikiCatalogQuery(
  row: { name: string; texts?: readonly string[] },
  query: string,
): boolean {
  const needle = query.trim().toLocaleLowerCase("ru-RU");
  if (!needle) {
    return true;
  }
  if (row.name.toLocaleLowerCase("ru-RU").includes(needle)) {
    return true;
  }
  return (row.texts ?? []).some((text) =>
    text.toLocaleLowerCase("ru-RU").includes(needle),
  );
}

export function filterWikiEntities<T extends { name: string }>(
  rows: T[],
  query: string,
): T[] {
  return rows.filter((row) => matchesWikiCatalogQuery(row, query));
}

export function wikiHref(entityId?: string): string {
  return entityId ? `/wiki/${entityId}` : "/wiki";
}

export function wikiGraphHref(entityId: string): string {
  return `/wiki/${entityId}/graph`;
}

export const wikiNavLabel = "Wiki";

export type WikiCrumb = {
  label: string;
  href?: string;
};

export function wikiTrail(...tail: WikiCrumb[]): WikiCrumb[] {
  return [
    tail.length > 0
      ? { href: "/wiki", label: wikiNavLabel }
      : { label: wikiNavLabel },
    ...tail,
  ];
}

export function claimEditsHref(claimId: string): string {
  return `/wiki/claims/${claimId}/edits`;
}

export const wikiEditedCopy = {
  badge: "изменён",
  hint: "Текст Wiki правили вручную. Документ не менялся.",
  title: "Изменения факта",
  description: "Документ-источник не менялся. Ниже — правки текста в Wiki.",
  empty: "Правок нет.",
  before: "Было",
  after: "Стало",
  back: "К Wiki",
} as const;

export function firstEditedClaimId(
  citations: ReadonlyArray<{ textEdited?: boolean; claimId?: string | null }>,
): string | undefined {
  return citations.find((item) => item.textEdited && item.claimId)?.claimId;
}

export const askWikiLabel = "Спросить AI";

export function askWikiHref(name: string): string {
  const query = name.trim();
  return query ? `/chat?q=${encodeURIComponent(query)}` : "/chat";
}

export function wikiReviewHref(claimId?: string): string {
  if (!claimId) {
    return "/wiki/review";
  }
  return `/wiki/review?id=${encodeURIComponent(claimId)}`;
}

export function parseWikiReviewClaimId(raw: string | undefined): string | null {
  const id = raw?.trim() ?? "";
  return id ? id : null;
}

export function claimConfidenceLabel(confidence: number): string {
  return `уверенность ${Math.round(confidence * 100)}%`;
}

/** На карточке сущности 100% не несёт сигнала. */
export function showClaimConfidence(confidence: number): boolean {
  return Math.round(confidence * 100) < 100;
}

export function wikiClaimCountLabel(count: number): string {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) {
    return `${count} фактов`;
  }
  if (n1 === 1) {
    return `${count} факт`;
  }
  if (n1 >= 2 && n1 <= 4) {
    return `${count} факта`;
  }
  return `${count} фактов`;
}

export const WIKI_REVIEW_EXCERPT_MAX = 600;

export function wikiReviewExcerpt(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) {
    return "";
  }
  if (trimmed.length <= WIKI_REVIEW_EXCERPT_MAX) {
    return trimmed;
  }
  return `${trimmed.slice(0, WIKI_REVIEW_EXCERPT_MAX).trim()}…`;
}

export function wikiReviewQueueLine(count: number): string {
  if (count <= 1) {
    return "";
  }
  return `Ещё ${wikiClaimCountLabel(count - 1)} после этого.`;
}

export function wikiReviewQueueSnippet(text: string): string {
  const clipped = text.trim();
  if (clipped.length <= 90) {
    return clipped;
  }
  return `${clipped.slice(0, 87).trim()}…`;
}

export function filterWikiReviewQueue<
  T extends { entityName: string; text: string },
>(rows: T[], query: string): T[] {
  return rows.filter((row) =>
    matchesWikiCatalogQuery({ name: row.entityName, texts: [row.text] }, query),
  );
}

export const WIKI_ENTITY_KEY_FACTS = 6;
export const WIKI_RELATION_SEARCH_MIN = 8;

export function filterWikiRelations<
  T extends { type: string; outgoing: boolean; otherName: string },
>(rows: T[], query: string): T[] {
  const needle = query.trim().toLocaleLowerCase("ru-RU");
  if (!needle) {
    return rows;
  }
  return rows.filter((row) =>
    relationPhrase(row.type, row.outgoing, row.otherName)
      .toLocaleLowerCase("ru-RU")
      .includes(needle),
  );
}

export function wikiFactsMoreLabel(count: number): string {
  return `Ещё ${wikiClaimCountLabel(count)}`;
}

/** Конфликты и короткие факты сверху; хвост — свернуть. Конфликты не прячем. */
export function splitWikiEntityFacts<
  T extends { text: string; conflicts: readonly unknown[] },
>(
  claims: T[],
  keyLimit = WIKI_ENTITY_KEY_FACTS,
): { key: T[]; tail: T[] } {
  const ranked = [...claims].sort((left, right) => {
    const leftConflict = left.conflicts.length > 0 ? 0 : 1;
    const rightConflict = right.conflicts.length > 0 ? 0 : 1;
    if (leftConflict !== rightConflict) {
      return leftConflict - rightConflict;
    }
    return left.text.length - right.text.length;
  });
  const conflictCount = ranked.filter((claim) => claim.conflicts.length > 0)
    .length;
  const shown = Math.max(keyLimit, conflictCount);
  return { key: ranked.slice(0, shown), tail: ranked.slice(shown) };
}

export function wikiConflictChipLabel(count: number): string {
  if (count <= 1) {
    return "разногласие";
  }
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) {
    return `${count} разногласий`;
  }
  if (n1 >= 2 && n1 <= 4) {
    return `${count} разногласия`;
  }
  return `${count} разногласий`;
}

/** Карточка сущности: конфликт — чип, не красный статус. */
export function wikiClaimCardStatus(status: string): string {
  if (status === "CONFLICTED") {
    return "PROPOSED";
  }
  return status;
}

export function relationTypeLabel(type: string, outgoing = true): string {
  if (type === "PARTY") return "сторона";
  if (type === "BELONGS_TO") return outgoing ? "относится к" : "включает";
  if (type === "REFERS_TO") return outgoing ? "ссылается на" : "на это ссылается";
  if (type === "SUPERSEDES") return outgoing ? "заменяет" : "заменён";
  return "связь";
}

export function wikiGraphLegendEdgeLabel(type: string): string {
  if (type === "PARTY") return wikiListCopy.relationsGraphLegendParty;
  if (type === "BELONGS_TO") return wikiListCopy.relationsGraphLegendBelongs;
  if (type === "REFERS_TO") return wikiListCopy.relationsGraphLegendRefers;
  if (type === "SUPERSEDES") return wikiListCopy.relationsGraphLegendSupersedes;
  return "связь";
}

export function wikiRelationChatSnippet(
  fromName: string,
  type: string,
  toName: string,
): string {
  return `${fromName.trim()}. ${relationPhrase(type, true, toName)}`;
}

export function chatCitationSourceMeta(item: {
  entityId?: string | null;
  heading: string | null;
  sectionPath: string;
  version: number;
}): string {
  if (item.entityId) {
    return wikiListCopy.sourceMeta;
  }
  return item.heading || item.sectionPath || `версия ${item.version}`;
}

export function relationPhrase(
  type: string,
  outgoing: boolean,
  otherName: string,
): string {
  const name = otherName.trim();
  if (type === "PARTY") {
    return outgoing ? `Сторона — ${name}` : `Сторона в ${name}`;
  }
  if (type === "BELONGS_TO") {
    return outgoing ? `Относится к ${name}` : `Включает ${name}`;
  }
  if (type === "REFERS_TO") {
    return outgoing ? `Ссылается на ${name}` : `На это ссылается ${name}`;
  }
  if (type === "SUPERSEDES") {
    return outgoing ? `Заменяет ${name}` : `Заменён: ${name}`;
  }
  return name;
}

export function claimConflictLine(partnerText: string): string {
  const clipped =
    partnerText.length > 100 ? `${partnerText.slice(0, 97).trim()}…` : partnerText;
  return `не сходится с: «${clipped}»`;
}

export function wikiClaimDisplayStatus(
  status: string,
  hasLivePartner: boolean,
): string {
  if (hasLivePartner) {
    return "CONFLICTED";
  }
  if (status === "CONFLICTED") {
    return "PROPOSED";
  }
  return status;
}

export function claimStatusLabel(status: string): string {
  if (status === "VERIFIED") return "Подтверждён";
  if (status === "CONFLICTED") return "Противоречие";
  if (status === "OUTDATED") return "Устарел";
  return "На проверке";
}

export function claimStatusKind(
  status: string,
): "green" | "red" | "blue" | "gray" {
  if (status === "VERIFIED") return "green";
  if (status === "CONFLICTED") return "red";
  if (status === "OUTDATED") return "gray";
  return "blue";
}
